import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { primaryRole } from '../../utils/roles';
import './auth.css';

// Where to send each role after a successful password reset + auto-login.
const ROLE_DASHBOARD = {
  contestant: '/contestant/dashboard',
  judge:      '/judge/dashboard',
  admin:      '/admin/dashboard',
  observer:   '/viewer/results',   // a viewer arriving from "Send login link"
};

export default function ResetPassword() {
  const [searchParams]  = useSearchParams();
  const token           = searchParams.get('token') || '';
  const navigate        = useNavigate();
  const { login }       = useAuth();

  // Token validation state
  const [validating,  setValidating]  = useState(true);
  const [tokenError,  setTokenError]  = useState('');
  const [resetEmail,  setResetEmail]  = useState('');

  // Form state
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw,          setShowPw]          = useState(false);
  const [formError,       setFormError]       = useState('');
  const [submitting,      setSubmitting]       = useState(false);

  // ── Validate token on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setTokenError('No reset token found. Please use the link from your password reset email.');
      setValidating(false);
      return;
    }
    api.get(`/api/auth/reset-password/${token}`)
      .then((res) => {
        setResetEmail(res.data.email);
        setValidating(false);
      })
      .catch((err) => {
        const msg = err.response?.data?.error || 'This link is invalid or has expired. Please request a new password reset.';
        setTokenError(msg);
        setValidating(false);
      });
  }, [token]);

  // ── Submit new password ───────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');

    if (!password)              { setFormError('Please enter a new password.'); return; }
    if (password.length < 8)   { setFormError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setFormError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const res = await api.post(`/api/auth/reset-password/${token}`, { password, confirmPassword });
      login(res.data.token, res.data.user);
      navigate(ROLE_DASHBOARD[primaryRole(res.data.user)] ?? '/login', { replace: true });
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to reset password. Please try again.');
      setSubmitting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="auth-page">
      <div className="auth-card">

        {/* Header */}
        <div className="auth-card__header">
          <img src="/assets/SCALA_logo.png" alt="SCALA logo" className="auth-foom__logo" />
          <div>
            <p className="auth-foom__subtitle">The SCALA Festival<br />of Original Music</p>
            <p className="auth-foom__name">FOOM</p>
            <p className="auth-foom__tagline">CELEBRATING SONGWRITING</p>
          </div>
        </div>

        <div className="auth-card__gold-divider" />

        <div className="auth-card__body">
          <h2 className="auth-form-heading">Reset Password</h2>

          {/* Loading */}
          {validating && (
            <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#6b7280' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
              Validating your reset link…
            </div>
          )}

          {/* Invalid / expired token */}
          {!validating && tokenError && (
            <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
              <p style={{ color: '#dc2626', fontWeight: 600, marginBottom: '0.75rem' }}>
                {tokenError}
              </p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.25rem' }}>
                Reset links expire after 1 hour. Please go back to the login page and request a new one.
              </p>
              <button
                className="auth-submit-btn"
                onClick={() => navigate('/login', { replace: true })}
              >
                Back to Sign In
              </button>
            </div>
          )}

          {/* Valid token — show reset form */}
          {!validating && !tokenError && (
            <form onSubmit={handleSubmit} noValidate>
              <p style={{ color: '#374151', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                Choose a new password for <strong>{resetEmail}</strong>.
              </p>

              {formError && (
                <div className="auth-alert" role="alert">{formError}</div>
              )}

              {/* Password */}
              <div className="form-group">
                <label className="form-label" htmlFor="rp-password">
                  New Password
                </label>
                <div className="input-wrapper">
                  <input
                    id="rp-password"
                    type={showPw ? 'text' : 'password'}
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label className="form-label" htmlFor="rp-confirm">
                  Confirm New Password
                </label>
                <input
                  id="rp-confirm"
                  type={showPw ? 'text' : 'password'}
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <><span className="spinner" aria-hidden="true" /> Saving…</>
                ) : (
                  'Set New Password'
                )}
              </button>

              <p style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
                <button
                  type="button"
                  className="auth-forgot-btn"
                  onClick={() => navigate('/login', { replace: true })}
                >
                  Back to Sign In
                </button>
              </p>
            </form>
          )}

        </div>{/* auth-card__body */}
      </div>
    </div>
  );
}
